import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  getRewards,
  getReward,
  createReward,
  updateReward,
  deleteReward,
  getRedemptionHistory,
  approveRedemption,
  rejectRedemption,
  getTeams
} from '../services/api';
import { getDateLocale } from '../i18n';

const RewardManagementPage = () => {
  const { t } = useTranslation(['admin', 'common']);
  const [rewards, setRewards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingReward, setEditingReward] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showRedemptionsModal, setShowRedemptionsModal] = useState(false);
  const [redemptions, setRedemptions] = useState([]);
  const [loadingRedemptions, setLoadingRedemptions] = useState(false);
  const [teams, setTeams] = useState([]);
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [activeTab, setActiveTab] = useState('rewards'); // 'rewards' or 'redemptions'
  const [redemptionStatusFilter, setRedemptionStatusFilter] = useState('all');
  const [showIconDropdown, setShowIconDropdown] = useState(false);

  // Icon options for dropdown (glyph values unchanged; labels translated)
  const iconOptions = [
    { value: 'card_giftcard', label: t('rewards.iconGiftCard') },
    { value: 'star', label: t('rewards.iconStar') },
    { value: 'school', label: t('rewards.iconTraining') },
    { value: 'local_offer', label: t('rewards.iconOffer') },
    { value: 'emoji_events', label: t('rewards.iconTrophy') },
    { value: 'shopping_bag', label: t('rewards.iconShopping') }
  ];

  const categoryLabel = (category) => {
    const keyMap = {
      monetary: 'rewards.categoryMonetary',
      training: 'rewards.categoryTraining',
      recognition: 'rewards.categoryRecognition',
      privileges: 'rewards.categoryPrivileges',
      other: 'rewards.categoryOther'
    };
    return keyMap[category] ? t(keyMap[category]) : (category || '');
  };

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    points_cost: 0,
    category: 'monetary',
    icon: 'card_giftcard',
    is_active: true,
    is_global: true,
    applicable_team_ids: [],
    min_points_required: null,
    requires_approval: true
  });

  useEffect(() => {
    fetchRewards();
    fetchTeams();
    if (activeTab === 'redemptions') {
      fetchRedemptions();
    }
  }, [activeTab, redemptionStatusFilter]);

  // Close icon dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showIconDropdown && !event.target.closest('.icon-dropdown-container')) {
        setShowIconDropdown(false);
      }
    };

    if (showIconDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showIconDropdown]);

  const fetchRewards = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getRewards();
      setRewards(response.results || response);
    } catch (err) {
      console.error('Failed to fetch rewards:', err);
      setError(t('rewards.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const fetchTeams = async () => {
    try {
      const response = await getTeams();
      setTeams(response.results || response);
    } catch (err) {
      console.error('Failed to fetch teams:', err);
    }
  };

  const fetchRedemptions = async () => {
    try {
      setLoadingRedemptions(true);
      const filters = {};
      if (redemptionStatusFilter !== 'all') {
        filters.status = redemptionStatusFilter;
      }
      const response = await getRedemptionHistory(filters);
      setRedemptions(response.results || response);
    } catch (err) {
      console.error('Failed to fetch redemptions:', err);
    } finally {
      setLoadingRedemptions(false);
    }
  };

  const handleEdit = async (reward) => {
    try {
      const rewardData = await getReward(reward.id);
      setEditingReward(rewardData);
      setFormData({
        name: rewardData.name || '',
        description: rewardData.description || '',
        points_cost: rewardData.points_cost || 0,
        category: rewardData.category || 'monetary',
        icon: rewardData.icon || 'card_giftcard',
        is_active: rewardData.is_active !== false,
        is_global: rewardData.is_global !== false,
        applicable_team_ids: rewardData.applicable_teams?.map(t => t.id) || [],
        min_points_required: rewardData.min_points_required || null,
        requires_approval: rewardData.requires_approval !== false
      });
      setShowEditModal(true);
    } catch (err) {
      console.error('Failed to fetch reward details:', err);
      setError(t('rewards.loadDetailsFailed'));
    }
  };

  const handleDelete = async (reward) => {
    if (!window.confirm(t('rewards.confirmDeleteNamed', { name: reward.name }))) {
      return;
    }

    try {
      setError(null);
      await deleteReward(reward.id);
      setSuccessMessage(t('rewards.deleted'));
      await fetchRewards();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Failed to delete reward:', err);
      setError(t('rewards.deleteFailed'));
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      
      const payload = {
        ...formData,
        min_points_required: formData.min_points_required || null
      };

      if (editingReward) {
        await updateReward(editingReward.id, payload);
        setSuccessMessage(t('rewards.updated'));
      } else {
        await createReward(payload);
        setSuccessMessage(t('rewards.created'));
      }

      setShowEditModal(false);
      setEditingReward(null);
      setShowIconDropdown(false);
      await fetchRewards();

      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Failed to save reward:', err);
      setError(err.data?.error || t('rewards.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setShowEditModal(false);
    setEditingReward(null);
    setShowIconDropdown(false);
    setFormData({
      name: '',
      description: '',
      points_cost: 0,
      category: 'monetary',
      icon: 'card_giftcard',
      is_active: true,
      is_global: true,
      applicable_team_ids: [],
      min_points_required: null,
      requires_approval: true
    });
  };

  const handleApprove = async (redemption) => {
    try {
      const notes = window.prompt(t('rewards.managerNotesPrompt'), '');
      await approveRedemption(redemption.id, notes || '');
      setSuccessMessage(t('rewards.approvedSuccess'));
      await fetchRedemptions();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Failed to approve redemption:', err);
      setError(t('rewards.approveFailed'));
    }
  };

  const handleReject = async (redemption) => {
    const reason = window.prompt(t('rewards.rejectionReasonPrompt'), '');
    if (!reason) {
      return;
    }
    
    try {
      const notes = window.prompt(t('rewards.managerNotesPrompt'), '');
      await rejectRedemption(redemption.id, reason, notes || '');
      setSuccessMessage(t('rewards.rejectedSuccess'));
      await fetchRedemptions();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Failed to reject redemption:', err);
      setError(t('rewards.rejectFailed'));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return t('common:na');
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(getDateLocale(), {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-900/40 dark:text-amber-200 dark:border-amber-700/70">
          <span className="material-symbols-outlined text-[14px]">schedule</span>
          {t('rewards.pending')}
        </span>
      ),
      approved: (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-200 dark:border-emerald-700/70">
          <span className="material-symbols-outlined text-[14px]">check_circle</span>
          {t('rewards.approved')}
        </span>
      ),
      rejected: (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/40 dark:text-red-200 dark:border-red-700/70">
          <span className="material-symbols-outlined text-[14px]">cancel</span>
          {t('rewards.rejected')}
        </span>
      )
    };
    return badges[status?.toLowerCase()] || badges.pending;
  };


  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">{t('rewards.title')}</h1>
          <p className="text-slate-600 mt-1">{t('rewards.subtitle')}</p>
        </div>
        {activeTab === 'rewards' && (
          <button
            onClick={() => {
              setEditingReward(null);
              setShowIconDropdown(false);
              setFormData({
                name: '',
                description: '',
                points_cost: 0,
                category: 'monetary',
                icon: 'card_giftcard',
                is_active: true,
                is_global: true,
                applicable_team_ids: [],
                min_points_required: null,
                requires_approval: true
              });
              setShowEditModal(true);
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            <span className="material-symbols-outlined text-[20px]">add</span>
            {t('rewards.createReward')}
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200">
        <nav className="flex gap-4">
          <button
            onClick={() => setActiveTab('rewards')}
            className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'rewards'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-slate-600 hover:text-slate-900'
            }`}
          >
            {t('rewards.tabRewards')}
          </button>
          <button
            onClick={() => {
              setActiveTab('redemptions');
              fetchRedemptions();
            }}
            className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'redemptions'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-slate-600 hover:text-slate-900'
            }`}
          >
            {t('rewards.tabRedemptions')}
          </button>
        </nav>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 dark:bg-red-900/30 dark:border-red-700/50 dark:text-red-200 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg">
          {successMessage}
        </div>
      )}

      {/* Rewards Tab */}
      {activeTab === 'rewards' && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">{t('rewards.colReward')}</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">{t('rewards.colCategory')}</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">{t('rewards.colPoints')}</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">{t('rewards.colStatus')}</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">{t('rewards.colScope')}</th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider">{t('rewards.colActions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {rewards.map((reward) => (
                  <tr key={reward.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <span className="material-symbols-outlined text-2xl text-blue-600">{reward.icon || 'card_giftcard'}</span>
                        <div>
                          <div className="font-semibold text-slate-900">{reward.name}</div>
                          {reward.description && (
                            <div className="text-sm text-slate-500 mt-1">{reward.description.substring(0, 50)}...</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">{categoryLabel(reward.category) || reward.category_display}</td>
                    <td className="px-6 py-4 text-sm font-semibold text-blue-600">{reward.points_cost}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${
                        reward.is_active
                          ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-200 dark:border-emerald-700/70'
                          : 'bg-gray-50 text-gray-700 border border-gray-200 dark:bg-slate-700/60 dark:text-slate-200 dark:border-slate-600/80'
                      }`}>
                        <span className="material-symbols-outlined text-[14px]">
                          {reward.is_active ? 'check_circle' : 'cancel'}
                        </span>
                        {reward.is_active ? t('common:active') : t('common:inactive')}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">
                      {reward.is_global ? t('rewards.global') : t('rewards.teamsCount', { count: reward.applicable_teams?.length || 0 })}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleEdit(reward)}
                          className="p-2 text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/40 rounded-lg transition-colors"
                          title={t('common:edit')}
                        >
                          <span className="material-symbols-outlined text-[20px]">edit</span>
                        </button>
                        <button
                          onClick={() => handleDelete(reward)}
                          className="p-2 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/40 rounded-lg transition-colors"
                          title={t('common:delete')}
                        >
                          <span className="material-symbols-outlined text-[20px]">delete</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {rewards.length === 0 && (
              <div className="text-center py-12 text-slate-500">
                {t('rewards.emptyState')}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Redemptions Tab */}
      {activeTab === 'redemptions' && (
        <div className="space-y-4">
          {/* Filter */}
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-slate-700">{t('rewards.filterByStatus')}</label>
            <select
              value={redemptionStatusFilter}
              onChange={(e) => setRedemptionStatusFilter(e.target.value)}
              className="px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
            >
              <option value="all">{t('common:all')}</option>
              <option value="pending">{t('rewards.pending')}</option>
              <option value="approved">{t('rewards.approved')}</option>
              <option value="rejected">{t('rewards.rejected')}</option>
            </select>
          </div>

          {/* Redemptions List */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
            {loadingRedemptions ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              </div>
            ) : redemptions.length === 0 ? (
              <div className="text-center py-12 text-slate-500">{t('rewards.noRedemptions')}</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">{t('rewards.colReward')}</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">{t('rewards.colEmployee')}</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">{t('rewards.colPoints')}</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">{t('rewards.colStatus')}</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">{t('rewards.colRequested')}</th>
                      <th className="px-6 py-3 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider">{t('rewards.colActions')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {redemptions.map((redemption) => (
                      <tr key={redemption.id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-6 py-4">
                          <div className="font-semibold text-slate-900">{redemption.reward_name || redemption.reward?.name}</div>
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600">{redemption.employee_name || redemption.employee?.email}</td>
                        <td className="px-6 py-4 text-sm font-semibold text-blue-600">{redemption.points_cost}</td>
                        <td className="px-6 py-4">{getStatusBadge(redemption.status)}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">{formatDate(redemption.requested_at)}</td>
                        <td className="px-6 py-4 text-right">
                          {redemption.status === 'pending' && (
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => handleApprove(redemption)}
                                className="px-3 py-1.5 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-700 transition-colors"
                              >
                                {t('rewards.approve')}
                              </button>
                              <button
                                onClick={() => handleReject(redemption)}
                                className="px-3 py-1.5 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors"
                              >
                                {t('rewards.reject')}
                              </button>
                            </div>
                          )}
                          {redemption.status !== 'pending' && redemption.processed_by_name && (
                            <div className="text-xs text-slate-500">
                              {t('rewards.processedBy', { name: redemption.processed_by_name })}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Edit Modal */}
      <AnimatePresence>
        {showEditModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6 border-b border-slate-200">
                <h2 className="text-2xl font-bold text-slate-900">
                  {editingReward ? t('rewards.editReward') : t('rewards.createReward')}
                </h2>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{t('rewards.rewardName')}</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 placeholder-slate-500 dark:placeholder-slate-400"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{t('common:description')}</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 placeholder-slate-500 dark:placeholder-slate-400"
                    rows="3"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">{t('rewards.category')}</label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 placeholder-slate-500 dark:placeholder-slate-400"
                    >
                      <option value="monetary">{t('rewards.categoryMonetary')}</option>
                      <option value="training">{t('rewards.categoryTraining')}</option>
                      <option value="recognition">{t('rewards.categoryRecognition')}</option>
                      <option value="privileges">{t('rewards.categoryPrivileges')}</option>
                      <option value="other">{t('rewards.categoryOther')}</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">{t('rewards.pointsCost')}</label>
                    <input
                      type="number"
                      min="0"
                      value={formData.points_cost}
                      onChange={(e) => setFormData({ ...formData, points_cost: parseInt(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 placeholder-slate-500 dark:placeholder-slate-400"
                      required
                    />
                  </div>
                </div>
                <div className="relative icon-dropdown-container">
                  <label className="block text-sm font-medium text-slate-700 mb-1">{t('rewards.iconLabel')}</label>
                  <button
                    type="button"
                    onClick={() => setShowIconDropdown(!showIconDropdown)}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 text-left flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <span className="material-symbols-outlined text-xl text-blue-600">{formData.icon}</span>
                      <span className="text-slate-700">{iconOptions.find(opt => opt.value === formData.icon)?.label || formData.icon}</span>
                    </div>
                    <span className="material-symbols-outlined text-slate-400">{showIconDropdown ? 'expand_less' : 'expand_more'}</span>
                  </button>
                  {showIconDropdown && (
                    <div className="absolute z-10 mt-1 w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg shadow-lg max-h-60 overflow-auto">
                      {iconOptions.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => {
                            setFormData({ ...formData, icon: option.value });
                            setShowIconDropdown(false);
                          }}
                          className={`w-full px-3 py-2 flex items-center gap-3 hover:bg-blue-50 transition-colors ${
                            formData.icon === option.value ? 'bg-blue-50' : ''
                          }`}
                        >
                          <span className="material-symbols-outlined text-xl text-blue-600">{option.value}</span>
                          <span className="text-slate-700">{option.label}</span>
                          {formData.icon === option.value && (
                            <span className="material-symbols-outlined text-blue-600 ml-auto text-lg">check</span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{t('rewards.minPointsRequired')}</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.min_points_required || ''}
                    onChange={(e) => setFormData({ ...formData, min_points_required: e.target.value ? parseInt(e.target.value) : null })}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 placeholder-slate-500 dark:placeholder-slate-400"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="is_active" className="text-sm font-medium text-slate-700">{t('common:active')}</label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_global"
                    checked={formData.is_global}
                    onChange={(e) => setFormData({ ...formData, is_global: e.target.checked, applicable_team_ids: e.target.checked ? [] : formData.applicable_team_ids })}
                    className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="is_global" className="text-sm font-medium text-slate-700">{t('rewards.globalReward')}</label>
                </div>
                {!formData.is_global && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">{t('rewards.applicableTeams')}</label>
                    <div className="max-h-40 overflow-y-auto border border-slate-300 rounded-lg p-2">
                      {teams.map((team) => (
                        <div key={team.id} className="flex items-center gap-2 py-1">
                          <input
                            type="checkbox"
                            id={`team-${team.id}`}
                            checked={formData.applicable_team_ids.includes(team.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setFormData({ ...formData, applicable_team_ids: [...formData.applicable_team_ids, team.id] });
                              } else {
                                setFormData({ ...formData, applicable_team_ids: formData.applicable_team_ids.filter(id => id !== team.id) });
                              }
                            }}
                            className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                          />
                          <label htmlFor={`team-${team.id}`} className="text-sm text-slate-700">{team.name}</label>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="requires_approval"
                    checked={formData.requires_approval}
                    onChange={(e) => setFormData({ ...formData, requires_approval: e.target.checked })}
                    className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="requires_approval" className="text-sm font-medium text-slate-700">{t('rewards.requiresApproval')}</label>
                </div>
              </div>
              <div className="p-6 border-t border-slate-200 flex items-center justify-end gap-3">
                <button
                  onClick={handleCancel}
                  className="px-4 py-2 text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
                  disabled={saving}
                >
                  {t('common:cancel')}
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving || !formData.name}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <span className="material-symbols-outlined text-[18px]">save</span>
                  <span>{t('common:save')}</span>
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default RewardManagementPage;

